import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import api from '@/lib/api';

const when = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const OrdersTable = ({ rows, testId, auditMode }) => (
  <div className="overflow-x-auto" data-testid={testId}>
    <table className="w-full text-sm">
      <thead>
        <tr className="text-left text-muted-foreground border-b">
          <th className="py-2 pr-3">Branch</th><th className="py-2 pr-3">Order #</th>
          <th className="py-2 pr-3">Service date</th><th className="py-2 pr-3">{auditMode ? 'Auto-closed at' : 'Kitchen status'}</th>
          <th className="py-2 pr-3">{auditMode ? 'Reason' : 'Order status'}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <tr key={r.id} className="border-b last:border-0" data-testid={`kac-row-${r.id}`}>
            <td className="py-2 pr-3">{r.branch_name || r.branch_id}</td>
            <td className="py-2 pr-3 font-medium">#{r.order_number}</td>
            <td className="py-2 pr-3">{r.service_date || r.kitchen_auto_closed?.service_date}</td>
            <td className="py-2 pr-3">{auditMode ? when(r.kitchen_auto_closed?.at) : r.kitchen_status}</td>
            <td className="py-2 pr-3 text-xs">{auditMode ? `${r.kitchen_auto_closed?.action} (was ${r.kitchen_auto_closed?.previous_kitchen_status}; not a kitchen confirmation)` : r.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const AdminPreview = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get('/kitchen-auto-close/preview').then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  if (!data) return null;
  const save = async (enabled) => {
    setBusy(true);
    try {
      await api.post('/kitchen-auto-close/settings', { enabled, include_backlog: false });
      toast.success(enabled ? 'Auto-close ON for new orders only (old orders untouched)' : 'Auto-close OFF');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not save'); } finally { setBusy(false); }
  };
  const on = !!data.settings?.enabled;
  return (
    <div className="space-y-3" data-testid="kac-admin-preview">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <Badge className={on ? 'bg-success/20 text-success' : 'bg-muted text-muted-foreground'} data-testid="kac-status-badge">Auto-close {on ? 'ON' : 'OFF'}</Badge>
        <span className="text-muted-foreground" data-testid="kac-backlog-count">Old pending kitchen orders that would close: {data.backlog_count}</span>
        <span className="text-muted-foreground">Protected: {data.protected.future_or_today} today/future, {data.protected.cancelled_or_refunded} cancelled/refunded</span>
        <Button size="sm" variant="outline" disabled={busy} onClick={() => save(!on)} data-testid="kac-toggle-btn">{on ? 'Turn OFF' : 'Turn ON (new orders only)'}</Button>
      </div>
      {data.eligible.length > 0 && <OrdersTable rows={data.eligible} testId="kac-preview-table" />}
    </div>
  );
};

export const KitchenAutoCloseCard = ({ date, branchId, isAdmin }) => {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get('/kitchen-auto-close/report', { params: { date, ...(branchId ? { branch_id: branchId } : {}) } })
      .then((r) => setRows(r.data.orders || [])).catch(() => setRows([]));
  }, [date, branchId]);
  return (
    <Card className="border-border" data-testid="kitchen-auto-close-card">
      <CardHeader><CardTitle className="font-outfit text-base">Kitchen orders auto-closed by system ({rows.length})</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {rows.length ? <OrdersTable rows={rows} testId="kac-report-table" auditMode /> : <p className="text-sm text-muted-foreground" data-testid="kac-report-empty">None for this service date.</p>}
        {isAdmin && <AdminPreview />}
      </CardContent>
    </Card>
  );
};
